import { Dialog, DialogBoundary, DialogClose, DialogTitle, DialogTrigger, useDialogClose } from "@suse-o11y/dialog";
import { IconClose } from "@suse-o11y/icons";
import { useComputed } from "@suse-o11y/signal";
import { usePermanent } from "@suse-o11y/use-permanent";
import React from "react";

import { Form } from "~/components/form";
import { FormField } from "~/components/form/field";
import { FormFieldReset } from "~/components/form/reset";

import type { TimeRange } from "../../app/state/view/timeline/TimeRange";
import type { DashboardVariable } from "../../common/dashboard-spec";
import { OBSERVABILITY_PROMQL_VARIABLES } from "../../routes/dashboards/built-in-variables";
import { DashboardSaveAlert } from "../../routes/dashboards/dashboard/save-alert";
import { SetupDashboardVariableDrawer } from "../_setup-dashboard-variable-drawer";

import { OBSERVABILITY_PROMQL_VARIABLE_TO_DESCRIPTION } from "./_built-in-variables";
import { BuiltInVariablesTable } from "./_built-in-variables-table";
import { init as initFormState } from "./_form-state";
import { VariablesTable } from "./_variables-table";

export interface DashboardVariablesDrawerProps {
	initial: ReadonlyArray<DashboardVariable>;
	timeRange: TimeRange;
	onSave: (variables: ReadonlyArray<DashboardVariable>) => void;
}

export const DashboardVariablesDrawer: React.FC<DashboardVariablesDrawerProps> = (props) => (
	<Dialog className="drawer/dialog drawer-wide">
		<DashboardVariablesDrawerContent {...props} />
	</Dialog>
);

const DashboardVariablesDrawerContent: React.FC<DashboardVariablesDrawerProps> = ({ timeRange, initial, onSave }) => {
	const close = useDialogClose();
	const state = usePermanent(() => initFormState(initial));
	const variables = useComputed((monitor) => state.getOutput(monitor) ?? [], [state]);

	return (
		<Form
			className="contents"
			onSubmit={(output) => {
				onSave(output);
				close();
			}}
			state={state}
		>
			<header className="drawer-header">
				<DialogTitle className="drawer-title">Edit Dashboard Variables</DialogTitle>

				<DialogClose aria-label="Close" className="button button-square ml-auto">
					<IconClose className="size-3" />
				</DialogClose>
			</header>

			<section className="drawer-body">
				<DashboardSaveAlert className="mb-4" placement="dashboard-variables-drawer" />

				<FormField className="items-stretch" state={state}>
					<VariablesTable timeRange={timeRange} variables={state} />

					<div className="mt-4 flex items-end justify-end gap-4">
						<FormFieldReset asChild>
							<button className="button button-secondary uppercase" type="button">
								Reset variables
							</button>
						</FormFieldReset>

						<DialogBoundary>
							<DialogTrigger className="button button-primary uppercase">Add variable</DialogTrigger>

							<SetupDashboardVariableDrawer
								onSave={(variable) => {
									state.setInput((current) => [...current, variable]);
								}}
								timeRange={timeRange}
								variables={variables}
							/>
						</DialogBoundary>
					</div>
				</FormField>

				<div className="mt-4">
					<BuiltInVariablesTable
						title="SUSE Observability PromQL variables"
						variables={OBSERVABILITY_PROMQL_VARIABLES.map((name) => ({
							name,
							description: OBSERVABILITY_PROMQL_VARIABLE_TO_DESCRIPTION[name],
						}))}
					/>
				</div>
			</section>

			<footer className="drawer-footer">
				<DialogClose className="button button-tertiary uppercase">Close</DialogClose>

				<button className="button button-primary uppercase" type="submit">
					Update variables
				</button>
			</footer>
		</Form>
	);
};
